import isEqual from 'lodash/isEqual';
// The module, not the package index — the index also re-exports
// `withDNDContext`, which is JSX inside a `.js` file and cannot be parsed
// by the test runner. `volto-subblocks` re-exports this very module, so
// the class reached here is the one volto-form-block extends.
//
// No `/src` segment: the package's `main` is `src/index.js`, so Volto
// resolves the package name to that directory already. Writing
// `volto-subblocks/src/SubblocksEdit` yields `src/src/...` and fails the
// webpack build while still resolving under plain Node — which is how it
// survived in a test file for so long.
import SubblocksEdit from 'volto-subblocks/SubblocksEdit';

const INSTALLED = '__aiEditorStateSync';

/**
 * Make volto-subblocks notice that its block data was replaced.
 *
 * `SubblocksEdit` copies `data.subblocks` into local state in
 * `componentDidMount` and never reads the prop again. Every edit made
 * through the editor also goes through its own handlers, so the copy stays
 * true — until something writes the page through Redux instead. An
 * assistant tool step does exactly that, and so does "Undo this change".
 * The form then keeps rendering the fields it saw when it mounted.
 *
 * The `componentDidUpdate` the block does not define, installed on the
 * prototype rather than patched into the file: pnpm reads
 * `patchedDependencies` only from the project root, so a patch cannot
 * travel with a published package — see the README caveat.
 *
 * Why not the remount used for the tabs block (remountOnStructureChange):
 * the two caches have different granularity. The tabs editor caches *which
 * tab is open*, so it only goes stale when the tab collection changes, and
 * a remount keyed on that is both sufficient and cheap. This caches *the
 * fields themselves*, including their labels, so it also goes stale when a
 * field is edited in place — and a key that noticed that would change on
 * every keystroke the user typed into a label, remounting the field and
 * taking the caret with it. Copying state back is the gentler correction,
 * and it is what the removed patch did.
 *
 * Idempotent, and additive: neither `SubblocksEdit` nor the `Edit` class
 * volto-form-block derives from it defines `componentDidUpdate` today. If
 * a future version grows one, it is called after ours rather than replaced.
 */
export default function installSubblocksStateSync() {
  const proto = SubblocksEdit?.prototype;
  if (!proto || proto[INSTALLED]) return false;

  const existing = proto.componentDidUpdate;

  proto.componentDidUpdate = function componentDidUpdate(...args) {
    const subblocks = this.props?.data?.subblocks || [];
    if (!isEqual(subblocks, this.state?.subblocks)) {
      this.setState({ subblocks });
    }
    if (existing) existing.apply(this, args);
  };

  proto[INSTALLED] = true;
  return true;
}
